import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/20/solid';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import Button from 'components/inputs/button';
import Select from 'components/inputs/select';
import Text from 'components/inputs/text';
import { OrchestratorTaskModelType } from 'data/types';
import { fetchDefaultAdminData } from 'features/admin';
import { useEffect, useMemo, useState } from 'react';
import axios from 'utils/api';
import { confirmationModal, errorModal, successModal } from 'utils/ui';

const EMPTY_TASK_MODEL: Omit<OrchestratorTaskModelType, 'id'> = {
  code: '',
  label: '',
  modelId: 0,
  fallbackModelId: null
};

export default function OrchestratorTaskModelsPage() {
  const dispatch = useAppDispatch();
  const { orchestratorTaskModels, orchestratorModels } = useAppSelector(
    (state) => state.admin
  );

  const [view, setView] = useState<'table' | 'detail'>('table');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [modelFilter, setModelFilter] = useState<string>('');

  const [code, setCode] = useState('');
  const [label, setLabel] = useState('');
  const [modelId, setModelId] = useState<number>(0);
  const [fallbackModelId, setFallbackModelId] = useState<number | null>(null);

  const selectedTaskModel =
    orchestratorTaskModels.find((t) => t.id === selectedId) ?? null;

  const filteredTaskModels = useMemo(() => {
    let tasks = orchestratorTaskModels;
    if (modelFilter) {
      tasks = tasks.filter((t) => t.modelId === Number(modelFilter));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      tasks = tasks.filter(
        (t) =>
          t.code.toLowerCase().includes(q) || t.label.toLowerCase().includes(q)
      );
    }
    return tasks;
  }, [orchestratorTaskModels, modelFilter, searchQuery]);

  const loadTaskModel = (taskModel: OrchestratorTaskModelType | null) => {
    if (taskModel) {
      setCode(taskModel.code);
      setLabel(taskModel.label);
      setModelId(taskModel.modelId);
      setFallbackModelId(taskModel.fallbackModelId);
    } else {
      setCode(EMPTY_TASK_MODEL.code);
      setLabel(EMPTY_TASK_MODEL.label);
      setModelId(orchestratorModels[0]?.id ?? 0);
      setFallbackModelId(null);
    }
  };

  useEffect(() => {
    if (isCreating) {
      loadTaskModel(null);
    } else {
      loadTaskModel(selectedTaskModel);
    }
  }, [selectedId, isCreating, orchestratorTaskModels]);

  const handleSelect = (id: number) => {
    setIsCreating(false);
    setSelectedId(id);
    setView('detail');
  };

  const handleNew = () => {
    setSelectedId(null);
    setIsCreating(true);
    loadTaskModel(null);
    setView('detail');
  };

  const handleBack = () => {
    setView('table');
    setSelectedId(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!code.trim() || !label.trim() || !modelId) {
      errorModal('All fields are required.');
      return;
    }

    const payload = {
      code: code.trim(),
      label: label.trim(),
      modelId,
      fallbackModelId
    };

    try {
      if (isCreating) {
        await axios.post('orchestrator-task-model', payload);
        dispatch(fetchDefaultAdminData());
        successModal('Task model created.');
      } else if (selectedId) {
        await axios.patch('orchestrator-task-model', {
          id: selectedId,
          ...payload
        });
        dispatch(fetchDefaultAdminData());
        successModal('Task model updated.');
      }
      setIsCreating(false);
      setSelectedId(null);
      setView('table');
    } catch {
      errorModal('Something went wrong.');
    }
  };

  const handleReset = () => {
    confirmationModal(
      'Are you sure you want to reset? All unsaved changes will be lost.',
      () => {
        if (isCreating) loadTaskModel(null);
        else loadTaskModel(selectedTaskModel);
      }
    );
  };

  const handleDelete = () => {
    if (!selectedId) return;
    confirmationModal(
      `Are you sure you want to delete "${selectedTaskModel?.label}"? This cannot be undone.`,
      async () => {
        try {
          await axios.delete(`orchestrator-task-model/${selectedId}`);
          setSelectedId(null);
          setView('table');
          dispatch(fetchDefaultAdminData());
          successModal('Task model deleted.');
        } catch {
          errorModal('Something went wrong.');
        }
      }
    );
  };

  const getModelLabel = (mid: number) =>
    orchestratorModels.find((m) => m.id === mid)?.label ?? '—';

  if (view === 'detail') {
    return (
      <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            onClick={handleBack}
            className='flex items-center gap-1 text-sm font-medium text-gray-500 hover:text-gray-700'
          >
            <ArrowLeftIcon className='h-4 w-4' />
            Back to Task Models
          </button>
        </div>

        <div className='text-2xl font-bold text-gray-900'>
          {isCreating
            ? 'New Task Model'
            : selectedTaskModel?.label ?? 'Edit Task Model'}
        </div>

        <div className='grid grid-cols-2 gap-4'>
          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Code</label>
            <Text
              value={code}
              onChange={(v) => setCode(v)}
              placeholder='e.g. chatbot-simple'
            />
          </div>

          <div className='flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Label</label>
            <Text
              value={label}
              onChange={(v) => setLabel(v)}
              placeholder='Display label'
            />
          </div>

          <div className='col-span-2 flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>Model</label>
            <Select
              value={modelId}
              onChange={(v) => setModelId(Number(v))}
              options={orchestratorModels.map((m) => ({
                label: m.label,
                value: m.id
              }))}
              preventDeselection
            />
          </div>

          <div className='col-span-2 flex flex-col gap-1'>
            <label className='text-sm font-semibold text-gray-700'>
              Fallback Model
            </label>
            <Select
              value={fallbackModelId ?? ''}
              onChange={(v) => setFallbackModelId(v === '' ? null : Number(v))}
              options={[
                { label: '(none)', value: '' },
                ...orchestratorModels.map((m) => ({
                  label: m.label,
                  value: m.id
                }))
              ]}
            />
          </div>
        </div>

        <div className='flex items-center gap-3 border-t border-gray-200 pt-4'>
          <Button
            text={isCreating ? 'Create Task Model' : 'Save Changes'}
            color='green'
            onClick={handleSave}
            disabled={!code.trim() || !label.trim()}
          />
          <Button text='Reset' color='white' onClick={handleReset} />
          {!isCreating && selectedId && (
            <div className='ml-auto'>
              <Button
                text='Delete Task Model'
                color='red'
                onClick={handleDelete}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className='mx-auto flex max-w-[1600px] flex-col gap-5'>
      <div className='text-3xl font-bold text-gray-900'>AI Task Models</div>

      <div className='flex items-end gap-3'>
        <div className='w-72'>
          <Text
            value={searchQuery}
            onChange={(v) => setSearchQuery(v)}
            placeholder='Search task models...'
            controlled
          />
        </div>
        <div className='w-56'>
          <Select
            value={modelFilter}
            onChange={(v) => setModelFilter(v.toString())}
            options={[
              { label: 'All Models', value: '' },
              ...orchestratorModels.map((m) => ({
                label: m.label,
                value: m.id
              }))
            ]}
          />
        </div>
        <div className='ml-auto'>
          <Button
            text='New Task Model'
            color='green'
            onClick={handleNew}
            icon={PlusIcon}
          />
        </div>
      </div>

      <div className='max-h-[65vh] overflow-auto'>
        <table className='min-w-full border-separate border-spacing-0 divide-y divide-gray-300'>
          <thead>
            <tr>
              {['Code', 'Label', 'Model'].map((header) => (
                <th
                  key={header}
                  scope='col'
                  className='sticky top-0 z-10 border-b border-gray-300 bg-gray-100 px-3 pb-3.5 pt-3.5 text-left text-sm font-semibold text-gray-900'
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className='divide-y divide-gray-200'>
            {filteredTaskModels.map((tm) => (
              <tr
                key={tm.id}
                onClick={() => handleSelect(tm.id)}
                className='cursor-pointer hover:bg-gray-50'
              >
                <td className='whitespace-nowrap px-3 py-3 text-sm font-medium text-gray-900'>
                  {tm.code}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {tm.label}
                </td>
                <td className='whitespace-nowrap px-3 py-3 text-sm text-gray-500'>
                  {getModelLabel(tm.modelId)}
                </td>
              </tr>
            ))}
            {filteredTaskModels.length === 0 && (
              <tr>
                <td
                  colSpan={3}
                  className='py-8 text-center text-sm text-gray-400'
                >
                  No task models found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className='text-sm text-gray-400'>
        {filteredTaskModels.length} task model
        {filteredTaskModels.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}
